import { getDuration } from '@common-fe/common-fe';
import axios, { AxiosHeaders, AxiosInstance, AxiosResponse } from 'axios';
import MockAdapter from 'axios-mock-adapter';

import { PATHS } from '@/common';
import { RequestHeaders, SERVER_ERRORS } from '@/common/constants';
import { REFRESH_TOKEN } from '@/common/paths';
import { getObservedOrganizationIdsFromURLSearchParamsAndToken } from '@/modules/core/hooks/useHistory';
import CONFIG, { stopDataDogSession } from '@/utils/config';
import Token, { IS_SSO_TOKEN } from '@/utils/handlers/Token';

import { useAuthStore } from '../modules/user/stores/useAuth.store';

import { TypedApiMethods } from './apiTypes';

export interface Response<T> extends AxiosResponse<T> {}
export interface RefreshBody {
  accessToken: string;
  refreshToken: string;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export interface ErrorResponse<T = any> extends AxiosResponse<T, AxiosHeaders> {
  response: {
    status: number;
    message: string;
  };
  config: {
    url?: string;
    headers: AxiosHeaders;
  };
}

export const BEARER = 'Bearer';
export const GATEWAY_KEY = 'gateway';
export const API_KEY = 'api';
export const UNAUTHORIZED_STATUS_CODE = 401;

const axiosInstance = axios.create({
  baseURL: CONFIG.BASE_URL,
}) as TypedApiMethods & AxiosInstance;

export const handleRefreshToken = async () => {
  const refreshToken = Token.getRefreshToken();
  const userName = Token.getUserName();

  if (Token.getSSODataFromHash()) {
    return;
  }

  if (!refreshToken && localStorage.getItem(IS_SSO_TOKEN) && Token.isSsoExpired()) {
    throw new Error();
  }

  if (!refreshToken || !userName) {
    return;
  }

  const response = await axiosInstance.post(REFRESH_TOKEN, {
    user_name: userName,
    refresh_token: refreshToken,
  });

  const newAccessToken = response?.data?.access_token;
  const expiresIn = response?.data?.expires_in;

  if (!newAccessToken || !expiresIn) {
    throw new Error();
  }

  Token.storeToken(newAccessToken);
  Token.storeExpiresIn(expiresIn);
};

export const forceRefreshToken = async () => {
  const { setAuth } = useAuthStore.getState();
  try {
    await handleRefreshToken();
  } catch {
    if (Token.isAuthenticated()) stopDataDogSession();
    Token.clear();
    setAuth(false);
  }
};

setInterval(
  () => {
    forceRefreshToken();
  },
  getDuration({ minutes: 4, seconds: 30 })
);

const getIsUnauthorizedException = (url?: string) => {
  if (!url) {
    return false;
  }

  return (
    url.includes(PATHS.CHANGE_PASSWORD) ||
    url.includes(PATHS.SIGN_IN) ||
    url.includes(PATHS.VERIFY_MFA_CODE) ||
    url.includes(PATHS.VERIFY_EXPRESS_SETUP_CODE) ||
    url.includes(PATHS.RESET_PASSWORD)
  );
};

export const errorHandler = async (error: ErrorResponse<unknown>) => {
  const { setAuth } = useAuthStore.getState();

  const isUnauthorizedException = getIsUnauthorizedException(error?.config?.url);

  // if (error?.response?.status === SERVER_ERRORS.UNAUTHORIZED || !error?.response?.status) {
  // TODO: Prevent kick off
  if (error?.response?.status === SERVER_ERRORS.UNAUTHORIZED && !isUnauthorizedException) {
    // Close the RUM session while the user is still attached to it (EL-30448).
    if (Token.isAuthenticated()) stopDataDogSession();
    Token.clear();
    setAuth(false);
    location.reload();
  }

  return new Promise((resolve, reject) => {
    reject(error);
  });
};

axiosInstance.interceptors.request.use(async (request) => {
  request.headers.Authorization = Token.getToken() ? `${BEARER} ${Token.getToken() || ''}` : undefined;
  return request;
});

const isGettingOrganizationData = (url?: string) => {
  if (!url) return false;

  const orgPathRegexp = new RegExp(/^\/organizations\/[0-9]*$/);

  return orgPathRegexp.test(url);
};
const isGettingContactData = (url?: string) => {
  if (!url) return false;

  const contactRegexp = new RegExp(/^\/contacts\/[0-9]*$/);

  return contactRegexp.test(url);
};
const isGettingTeamsProcessors = (url?: string) => {
  if (!url) return false;

  return url.startsWith(`${PATHS.CONTACTS_TEAMS}?`);
};
const isException = (url?: string) => {
  if (!url) return false;

  if (
    isGettingOrganizationData(url) ||
    url === PATHS.GET_ORGANIZATION_PROPERTIES_FIELD_STATE ||
    url === PATHS.PERSON_ORGANIZATIONS_HIERARCHY_BY_ID ||
    url === PATHS.ASSIGN_CLAIMS_TO_PROCESSOR ||
    url.endsWith('/overridable-properties/employee') ||
    isGettingTeamsProcessors(url) ||
    isGettingContactData(url)
  )
    return true;

  return false;
};
axiosInstance.interceptors.request.use(async (request) => {
  if (
    isException(request.url) ||
    request.headers[RequestHeaders.observedOrganizationPath] ||
    request.headers[RequestHeaders.observedOrganizationPath] === null
  )
    return request;
  const observedOrganizationPath = getObservedOrganizationIdsFromURLSearchParamsAndToken();

  request.headers[RequestHeaders.observedOrganizationPath] = observedOrganizationPath;

  return request;
});

axiosInstance.interceptors.response.use((response) => response, errorHandler);
/**
 * @deprecated  It's preferable to use MSW mocking from @/test
 */
const mock = new MockAdapter(axiosInstance, { delayResponse: 250, onNoMatch: 'passthrough' });

/**
 * Creates a mock AxiosResponse object for testing
 *
 * Usage:
 * vi.mocked(api.get).mockResolvedValue(mockAxiosResponse({ data: mockPayload }));
 */
function mockAxiosResponse<T>(partial: { data: T }): AxiosResponse<T> {
  return {
    data: partial.data,
    status: 200,
    statusText: 'OK',
    headers: {},
    config: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      headers: {} as any,
    },
  } as AxiosResponse<T>;
}
export { axiosInstance, mock, mockAxiosResponse };
